import get from 'lodash/get';
import debounce from 'lodash/debounce';

export const SECTION_GROUP_PREFIX = 'SectionGroup';
export const CONTENT_SECTIONS_PREFIX = 'ContentSections';

const CANVAS_ID = 'shopstory-canvas';
const SWELL_PAGE_ID = 'swell_page';
const SWELL_PAGE_INDEX = 0;
const FORM_UPDATE_DELAY = 500;

export const SWELL_PAGE_PREFIX = `${SWELL_PAGE_ID}.${SWELL_PAGE_INDEX}`;
export const SWELL_GLOBAL = 'swell_global';
export const SWELL_DEFAULT = 'swell_default';

function getEditorContext(context = window) {
  return context.editorWindowAPI?.editorContext;
}

export function getForm(context) {
  const editorContext = getEditorContext(context);

  return editorContext?.form;
}

function onFormUpdated({ values }) {
  const context = getEditorContext();

  context.backend.documents.update({
    entry: values,
  });
}

/** @returns {HTMLIFrameElement | null} */
export function getCanvasElement() {
  return document.getElementById(CANVAS_ID);
}

export function subscribeForm() {
  const form = getForm();

  return form.subscribe(debounce(onFormUpdated, FORM_UPDATE_DELAY), {
    values: true,
  });
}

export function getFormValues(context) {
  const form = getForm(context);

  return form?.values;
}

export function getFormItem(path, context) {
  const formValues = getFormValues(context);
  const [sectionId, blockId] = parseItemPath(path);
  const itemPath = blockId || sectionId;

  return itemPath ? get(formValues, addSwellPagePrefix(itemPath)) : null;
}

export function hasFormItem(path, context) {
  return Boolean(getFormItem(path, context));
}

export function changeFormValue(path, value, context) {
  const form = getForm(context);

  if (hasFormItem(path, context)) {
    form.change(path, value);
  }
}

/**
 * Updates multiple field values in a final-form instance at once.
 *
 * Executes all changes within a single `batch` call to prevent unnecessary
 * state updates or re-renders, improving performance.
 *
 * @param {Record<string, any>} values - An object where keys are field paths and
 *     values are the new values to set for those fields.
 * @param {Window | WindowProxy} context - The window or iframe context from which
 *     the form instance is retrieved using `getForm`.
 */
export function changeFormValues(values, context) {
  const form = getForm(context);

  form.finalForm.batch(() => {
    for (const [path, value] of Object.entries(values)) {
      form.change(path, value);
    }
  });
}

export function getDocument(pageId, easyblocksState) {
  if (!pageId) {
    return null;
  }

  return easyblocksState?.documents?.[pageId];
}

export function getEntryFromDocument(document) {
  return document?.entry;
}

export function getDocumentEntry(pageId, easyblocksState) {
  const document = getDocument(pageId, easyblocksState);

  return getEntryFromDocument(document);
}

export function getPageFromEntry(entry) {
  return entry?.[SWELL_PAGE_ID]?.[SWELL_PAGE_INDEX];
}

export function getPageFromDocument(document) {
  const entry = getEntryFromDocument(document);

  return getPageFromEntry(entry);
}

export function getDocumentPage(pageId, easyblocksState) {
  const entry = getDocumentEntry(pageId, easyblocksState);

  return getPageFromEntry(entry);
}

function getFocusedFieldFromContext(context) {
  // 'focussedField' is an array
  const field = context.focussedField || null;
  const hasFocusedField = field?.length > 0;

  return hasFocusedField ? field[0] : SWELL_GLOBAL;
}

export function getFocusedField() {
  const context = getEditorContext();

  if (!context) {
    return SWELL_GLOBAL;
  }

  return getFocusedFieldFromContext(context);
}

export function getParentFocusedField() {
  const context = getEditorContext(window.parent);

  if (!context) {
    return SWELL_GLOBAL;
  }

  return getFocusedFieldFromContext(context);
}

export function isSectionOrBlockPath(path) {
  if (typeof path !== 'string') {
    return false;
  }

  switch (path) {
    case SWELL_GLOBAL:
    case SWELL_PAGE_PREFIX:
    case SWELL_DEFAULT:
      return false;
    default:
      return true;
  }
}

export function parseItemPath(itemPath) {
  if (!isSectionOrBlockPath(itemPath)) {
    return [];
  }

  const path = removeSwellPagePrefix(itemPath);
  const [section, sectionIndex, block, blockIndex] = path.split('.');
  const sectionId = `${section}.${sectionIndex}`;
  let blockId = null;

  if (block) {
    blockId = `${sectionId}.${block}.${blockIndex}`;
  }

  return [sectionId, blockId];
}

export function addSwellPagePrefix(elementId) {
  return `${SWELL_PAGE_PREFIX}.${elementId}`;
}

export function removeSwellPagePrefix(elementId) {
  return (elementId || '').replace(`${SWELL_PAGE_PREFIX}.`, '');
}

function getPageSettingsFields(page) {
  return [
    ...(page.custom
      ? [
          {
            prop: 'slug',
            type: 'swell_short_text',
            label: 'Slug',
            description: 'A unique handle for the page',
            layout: 'column',
            isLabelHidden: true,
            required: true,
            defaultValue: page.slug,
            params: {
              slugify: true,
              localized: false,
            },
          },
        ]
      : []),
    ...(!page.record
      ? [
          {
            prop: 'title',
            type: 'swell_short_text',
            label: 'Page title',
            description: 'A page title used by search engines',
            layout: 'column',
            isLabelHidden: true,
            required: true,
            defaultValue: page.title,
          },
          {
            prop: 'description',
            type: 'swell_long_text',
            label: 'Description',
            description: 'A page description used by search engines',
            layout: 'column',
            isLabelHidden: true,
            defaultValue: page.description,
            params: {
              rows: 5,
              placeholder: 'Enter a description...',
            },
          },
        ]
      : []),
    {
      prop: '$locale',
      type: 'swell_locale',
      isLabelHidden: true,
    },
  ];
}

export function setPageSettingsFields(easyblocksConfig, page) {
  const swellPageComponent = easyblocksConfig?.components?.find(
    (component) => component.id === SWELL_PAGE_ID,
  );

  if (!swellPageComponent) {
    return;
  }

  swellPageComponent.schema.push(...getPageSettingsFields(page));
}

export function normalizeFocusTarget(target) {
  if (target === SWELL_DEFAULT) {
    return SWELL_DEFAULT;
  } else if (target === SWELL_GLOBAL) {
    return SWELL_GLOBAL;
  } else if (target.startsWith(SWELL_PAGE_PREFIX)) {
    return target;
  }

  return addSwellPagePrefix(target);
}

export function getFocusedFieldFromTarget(target) {
  const focusedField = typeof target !== 'string' ? SWELL_DEFAULT : target;

  switch (focusedField) {
    case SWELL_GLOBAL:
    case SWELL_PAGE_PREFIX:
    case SWELL_DEFAULT:
      return focusedField;
    default:
      break;
  }

  const [sectionId, blockId] = parseItemPath(focusedField);

  return blockId || sectionId || SWELL_DEFAULT;
}
